import { useMemo } from 'react';

import { useNotices } from '@/features/notices/useNotices';
import { useApi } from '@/shared/api';
import { useSjActions } from '@/shared/lib/sj-orm/hooks/useSjActions';
import { BeneficiaryDto } from '@/shared/lib/sj-orm/models/beneficiary.dto';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

export type BeneficiaryCreateData = Omit<BeneficiaryDto, 'id' | 'centralDbProfileId'>;

export const useBeneficiariesActions = () => {
  const keyPair = useKeyPairStore((s) => s.keyPair);
  const { create: beneficiariesCreate, ...beneficiariesActionsRest } =
    useSjActions('beneficiaries');

  const notices = useNotices();
  const { t } = useTranslation('entities/beneficiaries', { keyPrefix: 'errors' });
  const api = useApi();

  return useMemo(() => {
    const create = async (data: BeneficiaryCreateData): Promise<BeneficiaryDto | null> => {
      if (!keyPair?.publicKey) {
        return null;
      }

      const { data: centralDbBeneficiary } = await api.beneficiaryCreate({
        email: data.email,
        dontSendInviteEmail: false,
        isAnonymous: null,
      });

      if (!centralDbBeneficiary?.account.profileId) {
        notices.error(t('createBeneficiary'));
        return null;
      }

      const dto: BeneficiaryDto = {
        id: beneficiariesActionsRest.generateId(),
        ...data,
        centralDbProfileId: centralDbBeneficiary.account.profileId,
      };

      await beneficiariesCreate(dto, [keyPair.publicKey]);

      return dto;
    };

    const findByApiId = (id: number): BeneficiaryDto | undefined => {
      return beneficiariesActionsRest.findOne((item) => item.centralDbProfileId === id);
    };

    return { create, findByApiId, ...beneficiariesActionsRest };
  }, [keyPair, api, beneficiariesActionsRest]);
};
